import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider as ToastPrimitiveProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';

export type AppToastTone = 'success' | 'warning' | 'error';

type AppToastInput = {
  description: string;
  title?: string;
  duration?: number;
};

type AppToastRecord = AppToastInput & {
  id: string;
  open: boolean;
  tone: AppToastTone;
};

type ToastContextValue = {
  push: (input: AppToastInput & { tone?: AppToastTone }) => string;
  success: (description: string, options?: Omit<AppToastInput, 'description'>) => string;
  warning: (description: string, options?: Omit<AppToastInput, 'description'>) => string;
  error: (description: string, options?: Omit<AppToastInput, 'description'>) => string;
  dismiss: (id: string) => void;
};

export type ToastApi = Pick<
  ToastContextValue,
  'dismiss' | 'error' | 'push' | 'success' | 'warning'
>;

const noopToastApi: ToastContextValue = {
  push: () => '',
  success: () => '',
  warning: () => '',
  error: () => '',
  dismiss: () => {},
};

const ToastContext = createContext<ToastContextValue>(noopToastApi);
const DEFAULT_TOAST_DURATION_MS = 4000;
const TOAST_EXIT_ANIMATION_MS = 200;

const TOAST_ICONS: Record<AppToastTone, LucideIcon> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: CircleAlert,
};

function ToastIcon({ tone }: { tone: AppToastTone }) {
  const Icon = TOAST_ICONS[tone];
  return <Icon className="h-4 w-4 shrink-0 text-current" aria-hidden="true" />;
}

export function AppToastProvider({ children }: { children: ReactNode }) {
  const nextToastIdRef = useRef(0);
  const removalTimersRef = useRef(new Map<string, number>());
  const [toasts, setToasts] = useState<AppToastRecord[]>([]);

  const remove = useCallback((id: string) => {
    const timer = removalTimersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      removalTimersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const scheduleRemoval = useCallback(
    (id: string) => {
      if (removalTimersRef.current.has(id)) {
        return;
      }

      const timer = window.setTimeout(() => {
        removalTimersRef.current.delete(id);
        remove(id);
      }, TOAST_EXIT_ANIMATION_MS);
      removalTimersRef.current.set(id, timer);
    },
    [remove],
  );

  const dismiss = useCallback(
    (id: string) => {
      setToasts((current) =>
        current.some((toast) => toast.id === id && toast.open)
          ? current.map((toast) => (toast.id === id ? { ...toast, open: false } : toast))
          : current,
      );
      scheduleRemoval(id);
    },
    [scheduleRemoval],
  );

  const push = useCallback(
    ({ duration, tone = 'success', ...input }: AppToastInput & { tone?: AppToastTone }) => {
      const id = `toast-${nextToastIdRef.current++}`;
      const nextToast: AppToastRecord = {
        id,
        open: true,
        tone,
        duration: duration ?? DEFAULT_TOAST_DURATION_MS,
        ...input,
      };

      setToasts((current) => [...current, nextToast]);

      return id;
    },
    [],
  );

  const success = useCallback<ToastContextValue['success']>(
    (description, options) => push({ ...options, description, tone: 'success' }),
    [push],
  );
  const warning = useCallback<ToastContextValue['warning']>(
    (description, options) => push({ ...options, description, tone: 'warning' }),
    [push],
  );
  const error = useCallback<ToastContextValue['error']>(
    (description, options) => push({ ...options, description, tone: 'error' }),
    [push],
  );

  const value = useMemo(
    () => ({
      push,
      success,
      warning,
      error,
      dismiss,
    }),
    [dismiss, error, push, success, warning],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitiveProvider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            duration={toast.duration}
            open={toast.open}
            tone={toast.tone}
            onOpenChange={(open) => {
              if (!open) {
                dismiss(toast.id);
              }
            }}
          >
            <ToastIcon tone={toast.tone} />
            <div className="min-w-0 flex-1">
              {toast.title ? <ToastTitle>{toast.title}</ToastTitle> : null}
              <ToastDescription>{toast.description}</ToastDescription>
            </div>
            <ToastClose />
          </Toast>
        ))}
        {typeof document !== 'undefined' ? createPortal(<ToastViewport />, document.body) : null}
      </ToastPrimitiveProvider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
